import React, { memo, MouseEvent, useCallback } from 'react'

import { IconButton, Tooltip } from '..'
import { SmartAuditRuleCardProps } from './types'
import Svg from '../../common/svg/svg'
import { AuditRuleType, ItemSelection, RecurrenceType, ValueComparisonType } from '../../views/audits/smartAuditRules/types'
import { 
   expandMonthNumber,
   expandDayOfWeek,
} from './helpers'

import * as styles from './styles.module.scss'
import { format } from 'date-fns'

const SmartAuditRuleCard = ({
   smartAuditRule,
   onCardClick,
   onDeleteClick,
}: SmartAuditRuleCardProps) => {
   const handleCardEditClick = useCallback(
      (e: MouseEvent<HTMLElement>) => onCardClick(String(smartAuditRule.auditRuleID), e),
      [onCardClick, smartAuditRule.auditRuleID]
   )

   const handleDeleteClick = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
         e.stopPropagation()
         onDeleteClick(String(smartAuditRule.auditRuleID))
      },
      [onDeleteClick, smartAuditRule.auditRuleID]
   )

   const renderAuditType = () => {
      switch (smartAuditRule.auditRuleType as AuditRuleType) {
         case AuditRuleType.CONNECTION:
            return (
               <>
                  <p>
                     <Svg id={'smart-audit-worker'} className={styles.textIconSpacer} />
                     {smartAuditRule.connectionCount} {`${smartAuditRule.connectionCount === 1 ? `Connection` : `Connections`}`} 
                  </p>
               </>
            )
         case AuditRuleType.LOCATION:
            return (
               <>
                  <p>
                     <Svg id={'smart-audit-worker'} className={styles.textIconSpacer} />
                     {smartAuditRule.assignedTo}
                  </p>
                  <p>
                     <Svg id={'smart-audit-location-pin'} className={styles.textIconSpacer} />
                     {smartAuditRule.locationCount} {`${smartAuditRule.locationCount === 1 ? `Location` : `Locations`}`}
                  </p>
               </>
            )
      }
   }

   const computeItemSelectionString = () => {
      switch (smartAuditRule.itemSelectionType) {
         case ItemSelection.ALL_ASSIGNED_ITEMS:
            return 'All Assigned Items'
         case ItemSelection.ITEM_NOT_SCAN:
            return `Items Not Scanned in ${smartAuditRule.notScannedDays} days`
         case ItemSelection.ITEM_TAG_WITH_CATEGORY:
            return `${smartAuditRule.categoryCount} Categories`
         case ItemSelection.ITEM_WITH_VALUES:
            return `Items ${smartAuditRule.valueComparisonType === ValueComparisonType.OVER ? 'Over': 'Under'} ${smartAuditRule.comparisonValue} Value`
      }
   }

   const computeDayOfTheWeek = (weeklyAuditRule: any) => {
      const returnTrueItem = (object) => {
         return Object.keys(object).find(key =>
            object[key] === true);
      }

      const { everyXWeeks, ...rest } = weeklyAuditRule

      return returnTrueItem(rest)
   }

   const computeRecurrenceString = () => {
      const calculateTypeOfMonthlyRecurrence = (auditRuleMonthOrYear) => {
         if (auditRuleMonthOrYear.dayOfMonth) {
            return 'specific-day'
         }
         return 'declarative'
      }

      switch (smartAuditRule.recurrenceType as RecurrenceType) {
         case RecurrenceType.DAILY:
            const { everyXDays } = smartAuditRule.auditRuleDaily
            return `Every ${everyXDays === 1 ? 'Day' : `${everyXDays} Days`}`
         case RecurrenceType.WEEKLY:
            const { everyXWeeks } = smartAuditRule.auditRuleWeekly
            return `Every ${everyXWeeks === 1 ? `${computeDayOfTheWeek(smartAuditRule.auditRuleWeekly)}` : `${everyXWeeks} ${computeDayOfTheWeek(smartAuditRule.auditRuleWeekly)}s`}`
         case RecurrenceType.MONTHLY:
            const typeOfRecurrenceMonthly = calculateTypeOfMonthlyRecurrence(smartAuditRule.auditRuleMonthly)
            const { everyXMonths, dayOfMonth, weekOfMonth, weekday } = smartAuditRule.auditRuleMonthly

            if (typeOfRecurrenceMonthly === 'specific-day') {
               if (everyXMonths === 1) {
                  return `Monthly on day ${dayOfMonth}`
               }
               return `Every ${everyXMonths} Months on Day ${dayOfMonth}`
            }

            if (everyXMonths === 1) {
               return `Monthly on the ${weekOfMonth} ${expandDayOfWeek(weekday)}`
            }
            return `Every ${everyXMonths} months on the ${weekOfMonth} ${expandDayOfWeek(weekday)}`
         case RecurrenceType.YEARLY:
            const typeOfRecurrenceYearly = calculateTypeOfMonthlyRecurrence(smartAuditRule.auditRuleYearly)
            const {dayOfMonth: dayOfMonthYearly, monthNumber, weekOfMonth: weekOfMonthYearly, weekday: weekdayYearly} = smartAuditRule.auditRuleYearly

            const monthName = expandMonthNumber(monthNumber)

            if (typeOfRecurrenceYearly === 'specific-day') {
               return `Day ${dayOfMonthYearly} of every ${monthName}`
            }
            return `Every ${monthName} on the ${weekOfMonthYearly} ${expandDayOfWeek(weekdayYearly)}`
      }
   }

   return (
      <article
         className={`${styles.auditCard} ${smartAuditRule.isActive ? styles.enabled : styles.disabled}`}
         role="button"
         tabIndex={0}
         aria-label={smartAuditRule.auditRuleName}
         onClick={handleCardEditClick}
      >
         <header>
            <div className={styles.imageAndTitle}>
               <Svg id={'repeating-contract'}/>
               <h2>{smartAuditRule.auditRuleName}</h2>
            </div>
            <div className={styles.enabledStatus}>
               <h2>{smartAuditRule.isActive ? 'Enabled' : 'Disabled'}</h2>
            </div>
         </header>
         <section className={styles.sectionContent}>
            <div className={styles.contentBox}>
               <div className={styles.textContainer}>
                  {renderAuditType()}
               </div>
            </div>
            <div
               className={styles.contentBox}
            >
               <div className={styles.textContainer}>
                  <p>
                     <Svg id={'smart-audit-toolbox'} className={styles.textIconSpacer} />
                     {computeItemSelectionString()}
                  </p>
               </div>
            </div>
         </section>
         <footer>
            <Svg id={'calendar-clock'} className={styles.calendarClockCenterCorrect} />
            <div className={styles.footerText}>
               <p>
                  {computeRecurrenceString()}
                  {smartAuditRule.nextRunDate ? (` | Next Run ${format(new Date(smartAuditRule.nextRunDate), 'd MMM yyyy')}`) : null}
                  {smartAuditRule.lastRunDate ? (` | Last Run ${format(new Date(smartAuditRule.lastRunDate), 'd MMM yyyy')}`) : null}
               </p>
            </div>
            <div className={styles.buttonsContainer}>
               <Tooltip content="Edit">
                  <IconButton
                     id="edit-outlined"
                     action={handleCardEditClick}
                  />
               </Tooltip>
               <Tooltip content="Delete">
                  <IconButton
                     id="delete-outlined"
                     action={handleDeleteClick}
                  />
               </Tooltip>
            </div>
         </footer>
      </article>
   )
}

export default memo(SmartAuditRuleCard)
